import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  TablePagination,
} from "@mui/material";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import moment from "moment-timezone";
import { useNavigate, useSearchParams } from "react-router-dom";

import { endPoints } from "../../../constant/Environment";
import { getAllData, getAllFilterData } from "../../../Utility/API";
import Toaster from "../../../components/Toast";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import { tokens } from "../../../theme";
import { mono, pill, statusKind, th, td, filterFieldSx, TablePaginationActions } from "../../../components/tableKit";
import UserProfile from "./UserProfile";

const emptyFilters = {
  customerId: "",
  name: "",
  userName: "",
  email: "",
  state: "",
  acc_status: "",
  kyc_status: "",
  rg_limit: "",
  player_type: "",
};

const UserList = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [totalcount, setTotalCount] = useState(1);

  const [profileModal, setProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({});

  // Pre-filter by player type when arriving from a Segment ("View players").
  const [filters, setFilters] = useState({
    ...emptyFilters,
    player_type: searchParams.get("player_type") || "",
  });
  const [stateList, setStateList] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [kycList, setKycList] = useState([]);
  const [rgLimitList, setRgLimitList] = useState([]);
  const [playerList, setPlayerList] = useState([]);

  const setFilter = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
    fetchUserData(newPage + 1, filters);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
  };

  useEffect(() => {
    fetchUserData(page, filters);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchUserData(1, filters);
    fetchOptionLists();
  }, []);

  const fetchOptionLists = () => {
    getAllData(endPoints.api.STATES).then((r) => {
      if (r?.status === "success") setStateList(r.data || []);
    });
    getAllData(endPoints.api.accountstatus).then((r) => {
      if (r?.status === "success") setAccountList(r.data || []);
    });
    getAllData(endPoints.api.kycstatus).then((r) => {
      if (r?.status === "success") setKycList(r.data || []);
    });
    getAllData(endPoints.api.rgTypes).then((r) => {
      if (r?.status === "success") setRgLimitList(r.data || []);
    });
    getAllData(endPoints.api.playertypes).then((r) => {
      if (r?.status === "success") setPlayerList(r.data || []);
    });
  };

  const fetchUserData = async (page, data) => {
    let url = `${endPoints.api.GET_USERS}/${page}/${rowsPerPage}`;
    setIsLoading(true);
    await getAllFilterData(url, data)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") {
          let data = response.data;
          setRows(response.data);
          setTotalCount(data[0]?.totalcount);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const search = () => {
    setPage(1);
    fetchUserData(1, filters);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") search();
  };

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Users"
        crumb="Users / User List"
        subtitle="Search, review & manage player accounts"
        actions={
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddAltRoundedIcon />}
            onClick={() => navigate("/CreateUser")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add User
          </Button>
        }
      />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField size="small" label="Customer ID" value={filters.customerId} onChange={setFilter("customerId")} onKeyDown={handleKeyDown} sx={{ ...filterFieldSx, minWidth: 130 }} />
          <TextField size="small" label="Name" value={filters.name} onChange={setFilter("name")} onKeyDown={handleKeyDown} sx={{ ...filterFieldSx, minWidth: 140 }} />
          <TextField size="small" label="Username" value={filters.userName} onChange={setFilter("userName")} onKeyDown={handleKeyDown} sx={{ ...filterFieldSx, minWidth: 140 }} />
          <TextField size="small" label="Email" value={filters.email} onChange={setFilter("email")} onKeyDown={handleKeyDown} sx={{ ...filterFieldSx, minWidth: 160 }} />
          <TextField select size="small" label="State" value={filters.state} onChange={setFilter("state")} sx={{ ...filterFieldSx, minWidth: 150 }}>
            <MenuItem value="">All states</MenuItem>
            {stateList.map((item) => (
              <MenuItem key={item.state_id} value={item.state_id}>
                {`${item.state_name} (${item.state_code})`}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Account Status" value={filters.acc_status} onChange={setFilter("acc_status")} sx={{ ...filterFieldSx, minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            {accountList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.account_status}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="KYC Status" value={filters.kyc_status} onChange={setFilter("kyc_status")} sx={{ ...filterFieldSx, minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {kycList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.kyc_status}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="RG Limits" value={filters.rg_limit} onChange={setFilter("rg_limit")} sx={{ ...filterFieldSx, minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            {rgLimitList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Player Type" value={filters.player_type} onChange={setFilter("player_type")} sx={{ ...filterFieldSx, minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {playerList.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.player_type}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" onClick={search} sx={{ textTransform: "none" }}>
            Search
          </Button>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Player</th>
                <th style={th}>Email</th>
                <th style={th}>State</th>
                <th style={th}>Signup</th>
                <th style={th}>Account</th>
                <th style={th}>KYC</th>
                <th style={th}>Player Type</th>
                <th style={th}>Play</th>
                <th style={th}>RG Limits</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={10} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No users found
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.idUser}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{row.login}</div>
                    <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>
                      #{row.idUser} · {row.name} {row.surname}
                    </div>
                  </td>
                  <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{row.email}</td>
                  <td style={td}>{row.stateName}</td>
                  <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>
                    {moment(row.insertionTime).format("MM/DD/YYYY")}
                  </td>
                  <td style={td}>{row.accountStatus ? pill(row.accountStatus, statusKind(row.accountStatus)) : ""}</td>
                  <td style={td}>{row.kycStatus ? pill(row.kycStatus, statusKind(row.kycStatus)) : ""}</td>
                  <td style={td}>{row.playerType ? pill(row.playerType, "neutral") : ""}</td>
                  <td style={{ ...td, fontFamily: mono, fontWeight: 700 }}>
                    {row.multplier != null ? `${row.multplier}x` : ""}
                  </td>
                  <td style={td}>{row.rgType}</td>
                  <td style={td}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                        onClick={() => {
                          setProfileData(row);
                          setProfileModal(true);
                        }}
                      >
                        Update Profile
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
                        onClick={() => {
                          navigate({ pathname: `/UserDetails/${row.idUser}` });
                        }}
                      >
                        View
                      </Button>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          className="hide-rows-per-page-options"
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          count={rowsPerPage === -1 ? rows.length : totalcount || 0}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>

      <UserProfile
        open={profileModal}
        onClose={(flag) => {
          if (flag) {
            setProfileModal(false);
            fetchUserData(page, filters);
          } else {
            setProfileModal(false);
          }
        }}
        profileData={profileData}
      />
    </Box>
  );
};

export default UserList;
